// src/prf/prfQuote.js
// The deterministic PRF quote. One implementation, called by the quoter UI, the
// historical simulator and the optimizer alike — never re-derived locally.
//
// Verified against RMA's own pricing engine (GetPricingRates) rather than against
// a formula we believe. Reference case, grid 27508 / Genesee NY / haying
// non-irrigated / 85% / 150% productivity / 100 ac / 100% share /
// MAY-JUN 50% + JUL-AUG 50% / rainfall year 2025:
//
//   County Base Value          $447
//   Protection per acre        $569.925   = 447 x 0.85 x 1.50
//   MAY-JUN  protection $28,497  rate 0.0990  premium $2,821  subsidy $1,552  producer $1,269
//   JUL-AUG  protection $28,497  rate 0.0730  premium $2,080  subsidy $1,144  producer $936
//   TOTAL    protection $56,994               premium $4,901  subsidy $2,696  producer $2,205
//   JUL-AUG index 0.722 -> factor 0.150588 -> indemnity $4,291
//
// ⚠ THE ROUNDING ORDER IS LOAD-BEARING AND IS NOT THE OBVIOUS ONE.
// Total protection is $56,994, but protection-per-acre x acres is $56,992.50.
// They differ because RMA rounds PER INTERVAL and the total is the SUM OF THE
// ROUNDED INTERVALS -- it is not computed from the unrounded product. Subsidy
// rounds half-up, and producer premium is TOTAL MINUS SUBSIDY, not
// total x (1 - subsidy rate). Reproducing the arithmetic without reproducing
// the rounding gives numbers that are right to the dollar in most cases and
// wrong in the ones a grower notices.

const { paymentFactor } = require("./prfIndexMethodology.js");

// Half-up at a given number of decimals. JavaScript's toFixed rounds a binary
// float that merely looks like .5 the wrong way -- (5.085).toFixed(2) is "5.08" --
// so nudge by a relative epsilon before rounding. Same correction the Home page
// numeral guard needed.
function roundHalfUp(x, dp = 0) {
  const s = Math.pow(10, dp);
  const v = x * s;
  return Math.round(v + Math.sign(v) * Math.abs(v) * Number.EPSILON) / s;
}

// RMA's published subsidy schedule for plan 13 (ADM A00070). Passed in by the
// caller where possible; this is the cross-check, not the source.
const PRF_SUBSIDY = { 0.7: 0.59, 0.75: 0.59, 0.8: 0.55, 0.85: 0.55, 0.9: 0.51 };

// The national per-interval floor, PRF Crop Provisions (26-RI-PRF) §2(a).
const PRF_MIN_PERCENT_OF_VALUE = 0.10;

// ── BEGINNING FARMER AND RANCHER: EXTRA SUBSIDY POINTS ─────────────────────
// RMA Manager's Bulletin MGR-25-006 (One Big Beautiful Bill Act amendment):
// a beginning farmer or rancher receives the plan's subsidy PLUS 15 points in
// the 1st and 2nd BFR crop years, 13 in the 3rd, 11 in the 4th and 10 in the
// 5th through 10th. After the 10th the producer is no longer a BFR. The points
// are ADDED to the plan-13 rate for the coverage level (90% = 51%, so a 1st-year
// BFR at 90% is subsidised at 66%). Same ladder as src/bfrStatus.js
// BONUS_BY_YEAR; check-prf-bfr asserts the two are identical.
//
// Measured 2026-11-03: MP-0930254 bills $1,811 total and $1,195 subsidy, i.e.
// 0.6599 = 0.51 + 0.15. Pricing it at the base 51% put $887 producer premium
// on the farmer portal against $616 on the bill.
const PRF_BFR_BONUS_BY_YEAR = { 1: 0.15, 2: 0.15, 3: 0.13, 4: 0.11, 5: 0.10, 6: 0.10, 7: 0.10, 8: 0.10, 9: 0.10, 10: 0.10 };
const PRF_BFR_LAST_YEAR = 10;
// Which BFR years each bonus can mean. +15 and +10 do not pin a single year.
const PRF_BFR_YEARS_BY_BONUS = { 0.15: [1, 2], 0.13: [3], 0.11: [4], 0.10: [5, 6, 7, 8, 9, 10] };

function bfrBonusForYear(year) {
  if (year == null || year === "" || year === "none") return 0;
  const y = Number(year);
  if (!Number.isInteger(y) || y < 1) throw new Error(`prfQuote: BFR year ${year} is not a crop year count`);
  return y > PRF_BFR_LAST_YEAR ? 0 : PRF_BFR_BONUS_BY_YEAR[y];
}

// The BFR points a BILLED policy carries, read from its own subsidy. The AIP
// rounds subsidy per interval line to the dollar, so the ratio sits a little
// off the true rate; the tolerance is half the gap to the nearest other step
// (the steps are 0, 10, 11, 13, 15 points), so a ratio can only ever land on
// one of them. A ratio between steps returns null: "cannot tell", never "none".
const BFR_STEPS = [0, 0.10, 0.11, 0.13, 0.15];
function bfrFromBilled({ totalPremium, subsidy, coverageLevel }) {
  const t = Number(totalPremium), s = Number(subsidy);
  const base = PRF_SUBSIDY[Number(coverageLevel)];
  if (!(t > 0) || !Number.isFinite(s) || s < 0 || base == null) return null;
  const extra = s / t - base;
  let best = null, bestD = Infinity, bound = 0;
  for (const b of BFR_STEPS) {
    const d = Math.abs(extra - b);
    if (d >= bestD) continue;
    let gap = Infinity;
    for (const o of BFR_STEPS) if (o !== b) gap = Math.min(gap, Math.abs(o - b));
    best = b; bestD = d; bound = gap / 2;
  }
  if (!(bestD < bound)) return null;
  return { bonus: best, years: best ? PRF_BFR_YEARS_BY_BONUS[best] : [], ratio: s / t, base };
}

// The BFR year a RENEWAL assumes, one crop year on from the billed one. Where
// the billed bonus names two possible years (+15 = 1st or 2nd) the LATER one is
// assumed, so the renewal never quotes more subsidy than the farmer can have
// (1st -> 2nd keeps +15, 2nd -> 3rd drops to +13; +13 is assumed). +10 spans
// the 5th to 10th years: the renewal keeps +10 and says the 10th year is the
// last, because only the agent knows which year it is. The agent can override.
function bfrRenewalAssumption(observed) {
  if (!observed || !(observed.bonus > 0)) return { year: null, bonus: 0, label: null, assumed: false };
  const ys = observed.years || [];
  if (observed.bonus === 0.10) {
    return { year: null, bonus: 0.10, assumed: true,
             label: "BFR year 6–10 assumed (+10 pts) — no BFR after the 10th year" };
  }
  const next = Math.max(...ys) + 1;
  const bonus = bfrBonusForYear(next);
  return { year: next, bonus, assumed: true,
           label: `BFR year ${next} assumed (+${Math.round(bonus * 100)} pts)` };
}

// ── INDEX INTERVALS MAY NOT OVERLAP ────────────────────────────────────────
// The eleven intervals are consecutive TWO-MONTH windows, so neighbours share a
// month: MAY-JUN and JUN-JUL both contain June. Electing both insures June
// twice, and RMA does not write it.
//
// ⚠ SOURCED 2026-09-05 — RMA DOES PRINT IT, IN THE CROP PROVISIONS. This block
// used to say the rule was "not a quoted RMA sentence", because the Standards
// Handbook (FCIC-18150) §32.C sets the two-interval minimum and the 100% total
// and then defers the rest to "the CP and AD". The CP is where it lives —
// Rainfall Index PASTURE, RANGELAND, FORAGE Crop Provisions (26-RI-PRF) §2(b):
//
//   "you may select any index interval provided in the actuarial documents.
//    However, the same month cannot be included in more than one selected
//    index interval for the same county, grid ID, intended use, irrigated
//    practice, organic practice, and share. For example, if you select an
//    index interval that contains the months of April and May, you cannot
//    select any other index interval offered that contains either April or
//    May for the same county, grid ID, intended use, irrigated practice,
//    organic practice, and share."
//
//   https://www.rma.usda.gov/sites/default/files/2025-08/
//     Rainfall-Index-Pasture-Rangeland-Forage-Crop-Provisions-26-RI-PRF.pdf
//
// The two supports it rested on before are unchanged and still worth keeping,
// because they are what made it safe to ship before the citation was found:
// (1) Kevin, a licensed agent who sells the product, stating it directly, and
// (2) MEASUREMENT — across every real policy in the book, keyed the way RMA
// writes them (policy x grid x county x FARM UNIT), **384 of 384 cells elect
// no two consecutive intervals**. The published sentence now agrees with both.
//
// ⚠ NOTE WHAT THE SENTENCE ALSO SAYS: the prohibition is scoped to one county,
// grid, intended use, irrigation practice, ORGANIC practice and SHARE. Two
// different shares or practices on the same grid may each elect April. That is
// exactly the farm-unit trap recorded below, in RMA's own words.
//
// ⚠ AND THE FIRST MEASUREMENT SAID 3 VIOLATIONS, because it dropped farm_unit
// from the key and merged two units' separate elections into one apparent
// overlap. That is this repo's own rule biting the person applying it: a field
// carried on the row is not automatically a dimension, and dropping a real one
// invents a finding exactly as readily as adding a false one does.
//
// The refusal direction is the safe one: blocking an overlapping election can
// only stop us showing a prospect a quote an AIP would refuse to bind.
const IV_ORDER = ["JANFEB", "FEBMAR", "MARAPR", "APRMAY", "MAYJUN", "JUNJUL",
                  "JULAUG", "AUGSEP", "SEPOCT", "OCTNOV", "NOVDEC"];
const IV_MONTHS = {
  JANFEB: [1, 2], FEBMAR: [2, 3], MARAPR: [3, 4], APRMAY: [4, 5], MAYJUN: [5, 6],
  JUNJUL: [6, 7], JULAUG: [7, 8], AUGSEP: [8, 9], SEPOCT: [9, 10], OCTNOV: [10, 11],
  NOVDEC: [11, 12],
};

/**
 * Every pair of ELECTED intervals that share a month.
 * Returns [{ a, b, month }] — empty when the election is legal.
 * Compares MONTHS rather than list adjacency, so it stays correct if the
 * interval list is ever reordered.
 */
function overlappingIntervals(allocation) {
  const used = Object.keys(allocation || {})
    .filter((k) => Number(allocation[k]) > 0)
    .map((k) => String(k).toUpperCase())
    .filter((k) => IV_MONTHS[k])
    .sort((a, b) => IV_ORDER.indexOf(a) - IV_ORDER.indexOf(b));
  const bad = [];
  for (let i = 0; i < used.length; i++) {
    for (let j = i + 1; j < used.length; j++) {
      const shared = IV_MONTHS[used[i]].filter((m) => IV_MONTHS[used[j]].includes(m));
      if (shared.length) bad.push({ a: used[i], b: used[j], month: shared[0] });
    }
  }
  return bad;
}

// ── THE TWO NON-OVERLAPPING FAMILIES ──────────────────────────────────────
// Because neighbours share a month, only two maximal families of intervals can
// be elected together, and they are exactly the two rows Rain & Hail prints its
// allocation boxes in: the ODD-numbered starts (Jan-Feb, Mar-Apr, …) and the
// EVEN-numbered ones (Feb-Mar, Apr-May, …). They are DERIVED from IV_MONTHS
// rather than typed, so they cannot drift from the overlap rule they come from —
// and the checker asserts overlappingIntervals() finds nothing in either.
const IV_FAMILY_A = IV_ORDER.filter((_, i) => i % 2 === 0);   // 6: JANFEB … NOVDEC
const IV_FAMILY_B = IV_ORDER.filter((_, i) => i % 2 === 1);   // 5: FEBMAR … OCTNOV

/**
 * Spread 100% of value as evenly as whole percentages allow across `codes`.
 *
 * ⚠ IT REFUSES RATHER THAN ROUNDING INTO AN ILLEGAL ELECTION. RMA sets a
 * per-interval MINIMUM (10% in the Crop Provisions) and a MAXIMUM that is
 * county-specific and published in the Special Provisions — this repo already
 * reads both off the actuarials rather than hardcoding them. Six intervals
 * cannot be spread evenly under a minimum above 16%, and a button that silently
 * produced a 17/17/17/17/16/16 split an AIP would refuse is worse than no
 * button. Returns null with a reason instead.
 *
 * The remainder goes on the EARLIEST intervals, which is arbitrary but stated:
 * 100 does not divide by 6, and something has to carry the extra point.
 */
// ⚠ IT TAKES THE SAME `rules` SHAPE quotePrf VALIDATES ON — { IV: {min,max} },
// per interval, straight from RMA's GetMaxAcrePercentage. A scalar pair would
// have been a second, simpler idea of the limits sitting next to the real one,
// and the limits are genuinely per-interval and per-county (70% in Genesee NY,
// 50% in Tom Green TX). Where no rules are supplied it falls back to the Crop
// Provisions' national 10% floor and no ceiling, which is what the engine also
// does — it only enforces a maximum when one was given.
function spreadEvenly(codes, rules) {
  const list = (codes || []).filter((c) => IV_MONTHS[c]);
  if (list.length < 2) return { alloc: null, reason: "at least two index intervals must be elected" };
  const base = Math.floor(100 / list.length);
  const rem = 100 - base * list.length;
  const pct = list.map((_, i) => base + (i < rem ? 1 : 0));
  for (let i = 0; i < list.length; i++) {
    const r = (rules && rules[list[i]]) || {};
    const min = r.min == null ? 0.1 : Number(r.min);
    const max = r.max == null ? 1 : Number(r.max);
    const v = pct[i] / 100;
    if (v < min - 1e-9) {
      return { alloc: null, reason: `an even split over ${list.length} intervals puts ${pct[i]}% on ${list[i]}, below the ${Math.round(min * 100)}% minimum for this county` };
    }
    if (v > max + 1e-9) {
      return { alloc: null, reason: `an even split over ${list.length} intervals puts ${pct[i]}% on ${list[i]}, above the ${Math.round(max * 100)}% maximum for this county` };
    }
  }
  const alloc = {};
  list.forEach((c, i) => { alloc[c] = pct[i] / 100; });
  return { alloc, reason: null };
}

const MONTH_NAME = ["", "January", "February", "March", "April", "May", "June",
                    "July", "August", "September", "October", "November", "December"];

function overlapMessage(pairs) {
  const p = pairs[0];
  const lbl = (c) => c.slice(0, 3) + "-" + c.slice(3);
  return `${lbl(p.a)} and ${lbl(p.b)} both cover ${MONTH_NAME[p.month]} — ` +
         `index intervals may not overlap, so ${MONTH_NAME[p.month]} cannot be insured twice.`;
}

function subsidyRateFor(coverageLevel, provided) {
  if (provided != null) return Number(provided);
  const r = PRF_SUBSIDY[Number(coverageLevel)];
  if (r == null) throw new Error(`prfQuote: no subsidy rate for coverage ${coverageLevel}`);
  return r;
}
function checkBfrBonus(bfrBonus) {
  const extra = Number(bfrBonus) || 0;
  if (!(extra >= 0 && extra <= 0.15 + 1e-9)) throw new Error(`prfQuote: BFR bonus ${bfrBonus} outside MGR-25-006's 0-15 points`);
  return extra;
}

/**
 * Quote one PRF coverage block.
 *
 * @param {object} a
 * @param {number} a.countyBaseValue      CBV for the grid/type/practice
 * @param {number} a.coverageLevel        0.70 .. 0.90
 * @param {number} a.productivityFactor   0.60 .. 1.50
 * @param {number} a.acres                insured acres
 * @param {number} [a.share=1]            producer share, 0..1
 * @param {object} a.allocation           { INTERVALCODE: fractionOfValue }, summing to 1
 * @param {object} a.rates                { INTERVALCODE: baseRate } at this coverage level
 * @param {object} [a.indices]            { INTERVALCODE: finalIndex } to also compute indemnity
 * @param {number} [a.subsidyRate]        override; defaults to the plan-13 schedule
 * @param {number} [a.bfrBonus=0]         BFR extra subsidy points (MGR-25-006), added to the rate
 * @param {object} [a.rules]              { INTERVALCODE: {min,max} } per-county percent-of-value limits
 */
function quotePrf(a) {
  const {
    countyBaseValue: cbv, coverageLevel: cov, productivityFactor: pf,
    acres, share = 1, allocation, rates, indices, subsidyRate, rules, bfrBonus = 0,
  } = a;

  const incomplete = [];
  if (!Number.isFinite(cbv) || cbv <= 0) throw new Error("prfQuote: countyBaseValue is required");
  if (!Number.isFinite(acres) || acres <= 0) throw new Error("prfQuote: acres is required");
  if (!(cov > 0 && cov <= 1)) throw new Error(`prfQuote: coverageLevel ${cov} out of range`);
  if (!(pf >= 0.6 && pf <= 1.5)) throw new Error(`prfQuote: productivityFactor ${pf} outside RMA's 60%-150%`);
  if (!(share > 0 && share <= 1)) throw new Error(`prfQuote: share ${share} outside 0-100%`);

  const entries = Object.entries(allocation || {}).filter(([, p]) => Number(p) > 0);
  if (entries.length < 2) throw new Error("prfQuote: at least two index intervals must be elected");
  const total = entries.reduce((s, [, p]) => s + Number(p), 0);
  if (Math.abs(total - 1) > 1e-6) throw new Error(`prfQuote: percent of value must total 100%, got ${(total * 100).toFixed(2)}%`);
  // Refuse an overlapping election HERE, in the engine, so the optimizer, the
  // simulator, the compare table and the quoter all inherit it — a rule enforced
  // only in the UI is a rule three other callers do not have.
  const overlaps = overlappingIntervals(allocation);
  if (overlaps.length) throw new Error("prfQuote: " + overlapMessage(overlaps));
  // ⚠ TWO NATIONAL RULES HOLD WITH OR WITHOUT THE COUNTY'S OWN LIMITS. The
  // county table below is loaded for the book's grids only, so a prospect on any
  // other grid used to price an 8% interval or a 33.33% split without a word.
  //   · Percent of value is elected "in whole numbers" — Rainfall Index Plan
  //     Common Policy Basic Provisions (21-RI) §1, definition of percent of value.
  //   · No interval below 10% — PRF Crop Provisions (26-RI-PRF) §2(a). The
  //     MAXIMUM is set in the Special Provisions per county, so it stays a
  //     county rule and is still only enforced when `rules` are supplied.
  for (const [iv, p] of entries) {
    const pct = Number(p) * 100;
    if (Math.abs(pct - Math.round(pct)) > 1e-6) {
      throw new Error(`prfQuote: ${iv} at ${pct.toFixed(2)}% — percent of value is elected in whole percentages`);
    }
    if (Number(p) < PRF_MIN_PERCENT_OF_VALUE - 1e-9) {
      throw new Error(`prfQuote: ${iv} at ${Math.round(pct)}% is below the ${Math.round(PRF_MIN_PERCENT_OF_VALUE * 100)}% minimum in the PRF Crop Provisions`);
    }
  }
  // Per-county limits come from RMA (GetMaxAcrePercentage) and are NOT a national
  // constant -- 70% in Genesee NY, 50% in Tom Green TX. Only enforced when supplied.
  if (rules) {
    for (const [iv, p] of entries) {
      const r = rules[iv];
      if (!r) { incomplete.push(`no percent-of-value limits for ${iv}`); continue; }
      if (Number(p) < r.min - 1e-9) throw new Error(`prfQuote: ${iv} at ${(p * 100).toFixed(0)}% is below the ${(r.min * 100).toFixed(0)}% minimum`);
      if (Number(p) > r.max + 1e-9) throw new Error(`prfQuote: ${iv} at ${(p * 100).toFixed(0)}% exceeds the ${(r.max * 100).toFixed(0)}% maximum for this county`);
    }
  }

  const subs = subsidyRateFor(cov, subsidyRate);
  const bfr = checkBfrBonus(bfrBonus);
  const protectionPerAcre = cbv * cov * pf;
  // Round per acre to cents BEFORE applying acres and share. This is what makes
  // the reference case land on $28,497 rather than $28,496.
  const perAcreRounded = roundHalfUp(protectionPerAcre, 2);
  const blockProtection = perAcreRounded * acres * share;

  const lines = [];
  for (const [iv, pctRaw] of entries) {
    const pct = Number(pctRaw);
    const rate = rates?.[iv];
    if (rate == null) { incomplete.push(`no base rate for ${iv} at ${cov}`); continue; }
    const protection = roundHalfUp(blockProtection * pct, 0);
    const totalPremium = roundHalfUp(protection * Number(rate), 0);
    // ⚠ THE BFR POINTS ARE A SEPARATE ROUNDED AMOUNT, NOT A HIGHER RATE. The
    // AIP rounds the plan subsidy and the beginning-farmer subsidy to the dollar
    // EACH, then adds them. Measured on MP-0930254 (90%, +15), every line:
    //   $542: 276 + 81 = 357 billed   (one rate, 0.66 x 542 = 357.72, gives 358)
    //   $525: 268 + 79 = 347 billed   $162: 83 + 24 = 107   $582: 297 + 87 = 384
    // Separate rounding reproduces all four; a single 66% rate misses one.
    const baseSubsidy = roundHalfUp(totalPremium * subs, 0);
    const bfrSubsidy = bfr ? roundHalfUp(totalPremium * bfr, 0) : 0;
    const premiumSubsidy = baseSubsidy + bfrSubsidy;
    const producerPremium = totalPremium - premiumSubsidy;
    const line = {
      interval: iv, percent_of_value: pct, base_rate: Number(rate),
      protection, total_premium: totalPremium, premium_subsidy: premiumSubsidy,
      bfr_subsidy: bfrSubsidy,
      producer_premium: producerPremium,
    };
    const idx = indices?.[iv];
    if (idx != null && Number.isFinite(Number(idx))) {
      line.final_index = Number(idx);
      line.payment_factor = paymentFactor(cov, Number(idx));
      line.indemnity = roundHalfUp(protection * line.payment_factor, 0);
    }
    lines.push(line);
  }

  const sum = (k) => lines.reduce((s, l) => s + (l[k] || 0), 0);
  return {
    county_base_value: cbv, coverage_level: cov, productivity_factor: pf,
    acres, share, subsidy_rate: subs, bfr_bonus: bfr,
    protection_per_acre: protectionPerAcre,
    lines,
    // Totals are the SUM OF THE ROUNDED LINES, never the unrounded product.
    total_protection: sum("protection"),
    total_premium: sum("total_premium"),
    total_subsidy: sum("premium_subsidy"),
    producer_premium: sum("producer_premium"),
    indemnity: lines.some((l) => l.indemnity != null) ? sum("indemnity") : null,
    // A dropped interval makes a quote read LOW, and a low quote reads as good
    // news. It is returned so the caller can print it, never swallowed.
    incomplete,
  };
}

/**
 * Replay a strategy over a historical index series.
 * @param {object} a  same as quotePrf, plus:
 * @param {object} a.series  { INTERVALCODE: { year: index } }
 * @param {number[]} [a.years]
 */
function simulatePrf(a) {
  const { series, years: yearsIn } = a;
  const ivs = Object.keys(a.allocation).filter((k) => Number(a.allocation[k]) > 0);
  const years = yearsIn || [...new Set(ivs.flatMap((iv) => Object.keys(series[iv] || {}).map(Number)))].sort((x, y) => x - y);

  const rows = [];
  let skipped = 0;
  for (const y of years) {
    const indices = {};
    let complete = true;
    for (const iv of ivs) {
      const v = series[iv]?.[y];
      if (!Number.isFinite(v)) { complete = false; break; }
      indices[iv] = v;
    }
    // A partial year would understate that year's indemnity while charging full
    // premium — worse than omitting it. Omit and count.
    if (!complete) { skipped++; continue; }
    const q = quotePrf({ ...a, indices });
    rows.push({ year: y, indemnity: q.indemnity, producer_premium: q.producer_premium, net: q.indemnity - q.producer_premium });
  }
  if (!rows.length) return { years: 0, skipped, rows: [], summary: null };

  const paid = rows.filter((r) => r.indemnity > 0);
  const totI = rows.reduce((s, r) => s + r.indemnity, 0);
  const totP = rows.reduce((s, r) => s + r.producer_premium, 0);
  let run = 0, longestDry = 0;
  for (const r of rows) { run = r.indemnity > 0 ? 0 : run + 1; longestDry = Math.max(longestDry, run); }

  return {
    years: rows.length, skipped, rows,
    summary: {
      years: rows.length, years_paid: paid.length, payout_freq: paid.length / rows.length,
      total_indemnity: totI, total_producer_premium: totP, net: totI - totP,
      ratio: totP ? totI / totP : null,
      avg_net_per_year: (totI - totP) / rows.length,
      longest_dry_spell: longestDry,
      best: rows.reduce((x, y) => (y.net > x.net ? y : x)),
      worst: rows.reduce((x, y) => (y.net < x.net ? y : x)),
    },
  };
}

module.exports = { quotePrf, simulatePrf, roundHalfUp, PRF_SUBSIDY, PRF_MIN_PERCENT_OF_VALUE,
  PRF_BFR_BONUS_BY_YEAR, PRF_BFR_LAST_YEAR, PRF_BFR_YEARS_BY_BONUS,
  bfrBonusForYear, bfrFromBilled, bfrRenewalAssumption, subsidyRateFor,
  IV_ORDER, IV_MONTHS, IV_FAMILY_A, IV_FAMILY_B, spreadEvenly, overlappingIntervals,
                   overlappingIntervals, overlapMessage, IV_ORDER, IV_MONTHS };
